import { Link } from "react-router-dom";
import type { ArticleItem } from "@/mocks/news";

interface ArticleCardProps {
  article: ArticleItem;
  variant?: "feature" | "standard" | "row";
}

function MetaLine({ article, light = false }: { article: ArticleItem; light?: boolean }) {
  const tone = light ? "text-background-200" : "text-foreground-500";
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${tone}`}>
      <span className="flex items-center gap-1">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-user-line"></i>
        </span>
        {article.author}
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-time-line"></i>
        </span>
        {article.time}
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-chat-3-line"></i>
        </span>
        {article.comments.toLocaleString("zh-CN")}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = "standard" }: ArticleCardProps) {
  const href = `/article/${article.id}`;

  if (variant === "row") {
    return (
      <article className="group flex gap-4 py-4 border-b border-background-200 last:border-b-0">
        <Link to={href} className="shrink-0 w-24 h-20 sm:w-28 sm:h-20 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="min-w-0 flex flex-col justify-center gap-1.5">
          <Link
            to={`/category/${article.categorySlug}`}
            className="text-xs text-primary-600 hover:text-primary-700 transition-colors w-fit"
          >
            {article.categoryName}
          </Link>
          <h3 className="font-heading font-bold text-sm sm:text-base text-foreground-950 leading-snug line-clamp-2">
            <Link to={href} className="hover:text-primary-700 transition-colors">
              {article.title}
            </Link>
          </h3>
          <MetaLine article={article} />
        </div>
      </article>
    );
  }

  if (variant === "feature") {
    return (
      <article className="group relative w-full rounded-lg overflow-hidden bg-foreground-950 min-h-[320px] md:min-h-[420px] flex items-end">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-80 group-hover:opacity-90 transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/55 to-transparent"></div>
        <div className="relative z-10 p-5 md:p-7 w-full">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-flex items-center h-6 px-2.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors"
            >
              {article.categoryName}
            </Link>
            {article.isBreaking && (
              <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-md bg-accent-500 text-foreground-950 text-xs font-medium">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-flashlight-fill"></i>
                </span>
                正在跟进
              </span>
            )}
          </div>
          <h3 className="font-heading font-black text-xl md:text-3xl text-background-50 leading-snug mb-3 max-w-3xl">
            <Link to={href} className="hover:text-accent-300 transition-colors">
              {article.title}
            </Link>
          </h3>
          <p className="hidden sm:block text-sm text-background-200 leading-relaxed max-w-2xl mb-4 line-clamp-2">
            {article.excerpt}
          </p>
          <MetaLine article={article} light />
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col h-full bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={href} className="block w-full h-44 sm:h-48 overflow-hidden bg-background-200">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <Link
            to={`/category/${article.categorySlug}`}
            className="inline-flex items-center h-6 px-2.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors"
          >
            {article.categoryName}
          </Link>
          <span className="text-xs text-foreground-400 flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-flight-takeoff-line"></i>
            </span>
            {article.readTime}
          </span>
        </div>
        <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug mb-2 line-clamp-2">
          <Link to={href} className="hover:text-primary-700 transition-colors">
            {article.title}
          </Link>
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="mt-auto pt-3 border-t border-background-200">
          <MetaLine article={article} />
        </div>
      </div>
    </article>
  );
}